import { MouseEvent, useState, useContext } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Title from '../../shared/Title';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import DropDownButton, { DropDownMenu } from '../../shared/DropDownButton';
import TooltipDiv from '../../shared/TooltipDiv';
import ChartContainer from '../../shared/ChartContainer';
import { useTranslation } from 'react-i18next';
import { TrafficAnalyticsContext } from '../../../context/trafficanalytics-context';
import { DateValue, ValueDictNumber } from '../../../model/base';
import BlueSkeleton from '../../shared/BlueSkeleton';

interface ChartProps {
  data?: ValueDictNumber;
}

const BarTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <TooltipDiv>{payload[0].value} People</TooltipDiv>
    );
  }

  return null;
};

const processData = (data: ValueDictNumber | undefined) => {

  const newData: DateValue[] = [];
  if (data) {
    Object.entries(data.value).forEach(



      ([key, val]) => {
        const theDate = new Date(Date.parse(key));
        const dateString = theDate.toLocaleDateString("en-US", { month: 'short' }) + " " + theDate.toLocaleDateString("en-US", { day: 'numeric' });
        newData.push({
          date: dateString,
          value: val
        });
      }
    );
  }

  return newData;
};


function TrafficLineChart({ data }: ChartProps) {
  const theme = useTheme();

  const formattedData = processData(data);

  return (
    <ResponsiveContainer >
      <LineChart
        data={formattedData}
        margin={{
          top: 16,
          right: 16,
          bottom: 0,
          left: 0,
        }}
      >
        <XAxis
          dataKey="date"
          stroke={theme.palette.text.primary}
          tickLine={false}
          strokeDasharray="3 3"
          style={{
            fontSize: '0.9rem',
            fontFamily: 'Montserrat',
          }}
        />
        <YAxis
          stroke={theme.palette.text.secondary}
          tickLine={false}
          axisLine={false}
          style={{
            fontSize: '0.9rem',
            fontFamily: 'Montserrat',
            fontWeight: 100
          }}
        />

        <CartesianGrid stroke={theme.palette.shadow.dark} strokeDasharray="3 3" vertical={false} />
        <Line
          isAnimationActive={false}
          type="monotone"
          dataKey="value"
          stroke={theme.palette.secondary.main}
          strokeWidth={5}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )

}


function TrafficBarChart({ data }: ChartProps) {
  const theme = useTheme();
  const formattedData = processData(data);

  return (
    <ResponsiveContainer width="100%" height="100%" className="hidden" >
      <BarChart
        width={500}
        height={300}
        data={formattedData}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        barSize={30}
      >
        <CartesianGrid stroke={theme.palette.shadow.dark} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          stroke={theme.palette.text.primary}
          tickLine={false}
          axisLine={false}
          strokeDasharray="3 3"
          style={{
            fontSize: '0.9rem',
            fontFamily: 'Montserrat',
          }}
        />
        <YAxis
          stroke={theme.palette.text.secondary}
          tickLine={false}
          axisLine={false}
          style={{
            fontSize: '0.9rem',
            fontFamily: 'Montserrat',
            fontWeight: 100
          }}
        />
        <Bar dataKey="value" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]} />
        <Tooltip cursor={false} content={BarTooltip} />
      </BarChart>
    </ResponsiveContainer>
  )

}

const exportMenus: DropDownMenu[] = [{ label: 'PDF', value: 'pdf' }, { label: 'CSV', value: 'csv' }];

export const filterMenus: DropDownMenu[] = [
  { label: 'By Week', value: 'week' },
  { label: 'By Month', value: 'month' },
  { label: 'By Year', value: 'year' }
];

export default function TrafficCard() {
  const [trafficChart, setTrafficChart] = useState('bar');
  const { t } = useTranslation();
  const { status, data, isFetching } = useContext(TrafficAnalyticsContext);

  function toggleBarGraph(e: MouseEvent) {
    setTrafficChart('bar');
  }

  function toggleLineGraph(e: MouseEvent) {
    setTrafficChart('line');
  }

  return (
    <Grid item={true} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column', height: 320,
        alignItems: 'stretch',
        position: 'relative',
        justifyContent: 'stretch',
        border: '0 0 0 0 solid',
      }}>

        <Stack direction="column"
          justifyContent="center"
          alignItems="stretch"
          spacing={1}
          sx={{
            height: '100%',
          }}
        >
          <Stack direction="row" spacing={2} sx={{flexWrap: 'wrap'}}>
            <Title big={true} notooltip={true}>Traffic</Title>
            <Stack direction="row" spacing={2} sx={{ pr: 1, pb: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button variant="contained"
                disableElevation={true}
                onClick={toggleBarGraph}
                sx={{
                  backgroundColor: trafficChart === 'bar' ? 'text.primary' : 'action.active',
                  color: trafficChart === 'bar' ? 'white' : 'text.primary'
                }} >Bar Graph</Button>
              <Button variant="contained"
                disableElevation={true}
                onClick={toggleLineGraph}
                sx={{
                  backgroundColor: trafficChart === 'line' ? 'text.primary' : 'action.active',
                  color: trafficChart === 'line' ? 'white' : 'text.primary'
                }}>Line Graph</Button>
              <DropDownButton boxShadow={false}
                defaultLabel={t('app_export_data')}
                color="text.primary" backgroundColor="button.main" fontSize={14} data={exportMenus} border={true} />
              <DropDownButton defaultLabel='By Week' fontSize={15} data={filterMenus} border={false} boxShadow={false} />
            </Stack>
          </Stack>

          {isFetching && <BlueSkeleton width={'100%'} height={'100%'} sx={{ mt: 1, ml: 'auto', mr: 'auto' }} />}

          {status === 'success' && !isFetching && <ChartContainer>
            {trafficChart === 'bar' && <TrafficBarChart data={data?.trafficAnalyticsGraph} />}
            {trafficChart === 'line' && <TrafficLineChart data={data?.trafficAnalyticsGraph} />}
          </ChartContainer>}

        </Stack>

      </Box>
    </Grid>
  )

}